import { usePreview } from "@core/hooks/usePreview";
import { getModelHash, ModelData } from "@/data/data";
import { ARButton } from "@components/ARButton";
import { PreviewInteractionHint } from "@components/PreviewInteractionHint";
import LoggerheadImage1 from "@assets/turtle-images/loggerhead-1.webp";
import LoggerheadImage2 from "@assets/turtle-images/loggerhead-2.webp";
import LoggerheadImage3 from "@assets/turtle-images/loggerhead-3.webp";
import LoggerheadImage4 from "@assets/turtle-images/loggerhead-4.webp";

interface LoggerheadSectionProps {
  model: ModelData;
  onLaunchAR: (model: ModelData) => void;
  onTrackLaunchClick?: (
    model: ModelData,
    options?: { transportType?: "beacon" }
  ) => void;
  launchUrl?: string;
  isChecking: boolean;
  isCompatible: boolean;
}

export function LoggerheadSection({
  model,
  onLaunchAR,
  onTrackLaunchClick,
  launchUrl,
  isChecking,
  isCompatible,
}: LoggerheadSectionProps) {
  const { canvasRef, isFullscreen } = usePreview(model.id);

  const loggerheadCopy = {
    row1: {
      left: {
        title: "A big-headed crab-crusher",
        image: LoggerheadImage1,
        imageAlt: "Loggerhead turtle close-up",
        imageCaption: (
          <>
            Photo by{" "}
            <a href="https://www.flickr.com/photos/myfwc/15410581132" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70">Blair Witherington</a>,{" "}
            <a href="https://creativecommons.org/licenses/by-nc-nd/2.0/deed.en" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70">CC BY-NC-ND 2.0</a>
          </>
        ),
        useImageCaptionSpacer: true,
        paragraphs: [
          "Loggerhead sea turtles get their name from their oversized head, which looks a bit like – you guessed it! – a big log.",
          "They have super strong jaws to crush prey such as crabs and clams, and they eat a lot.",
          "Loggerheads take in saltwater and get rid of the salt through glands near their eyes – those aren’t sad tears.",
          "They weigh about as much as 3 people, up to 200 kg. Loggerheads are the second largest of the hard-shelled turtles.",
        ],
      },
      right: {
        title: "Protect our special visitors",
        image: LoggerheadImage2,
        imageAlt: "Loggerhead turtle in ocean habitat",
        imageCaption: (
          <>
            Photo by{" "}
            <a href="https://www.flickr.com/photos/myfwc/15410581132" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70">Blair Witherington</a>,{" "}
            <a href="https://creativecommons.org/licenses/by-nc-nd/2.0/deed.en" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70">CC BY-NC-ND 2.0</a>
          </>
        ),
        paragraphs: [
          "Loggerhead sea turtles are in danger! We’ve lost 80% of loggerheads from eastern Australia since the 1970s.",
          "In Australia, loggerheads are endangered, but the local population is <span class='italic'>critically</span> endangered. This is the final category before being listed as <span class='italic'>extinct</span> in the wild.",
          "The nesting loggerheads at Mon Repos to Wreck Rock are threatened by feral pigs and foxes digging up eggs, people disturbing nests, coastal development, and entanglement in fishing gear or shark nets.",
        ],
      },
    },
    row2: {
      left: {
        title: "Long-distance travellers beware!",
        image: LoggerheadImage3,
        imageAlt: "Loggerhead migration tracking map",
        imageCaption: (
          <>
            Photo by{" "}
            <a href="https://www.flickr.com/photos/myfwc/15224158689" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70">Blair Witherington</a>,{" "}
            <a href="https://creativecommons.org/licenses/by-nc-nd/2.0/deed.en" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70">CC BY-NC-ND 2.0</a>
          </>
        ),
        paragraphs: [
          "Loggerhead sea turtle hatchlings ride the East Australian Current from our coast past New Zealand and then across the Pacific, journeying 20,000+ km.",
          "Then, mysteriously, ‘the lost years’: loggerheads spend 10–16 years around South America before returning to Australia.",
          "We do know that loggerheads are in danger from being caught in South America’s longline fishing set ups. Australian trawlers use turtle exclusion devices which allows turtles to escape.",
        ],
      },
      right: {
        title: "Log a loggerhead",
        image: LoggerheadImage4,
        imageAlt: "Rescued loggerhead turtle story image",
        imageCaption: "TOD ready for release. Photo by TITR",
        paragraphs: [
          {
            text: "Just after New Year’s 2025, it’s sunrise and the tide is out on Sirenia Beach, leaving a turtle stranded. A visitor calls Turtles in Trouble Rescue (TITR).",
            italic: true,
          },
          "TITR rush out to discover an underweight loggerhead with a partially healed, amputated front flipper.",
          "TOD, as it was named, was treated to reduce sandy grit compacting its gut and given a high-protein diet to rebuild its strength. TOD was released a month later with a satellite tag that showed is initially stayed near the release spot.",
        ],
      },
    },
  };

  const handleLaunchAR = (selectedModel: ModelData) => {
    if (selectedModel.arType === "webxr" && isCompatible) {
      console.log("[View AR / App Clip] launchUrl:", launchUrl);
      onLaunchAR(selectedModel);
    }
  };

  const renderModelViewer = () => (
    <div className="w-full relative h-[300px] sm:h-[400px] md:h-[549px]">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-[60]"
        tabIndex={-1}
        style={{ width: "100%", height: "100%", outline: "none" }}
      />
    </div>
  );

  const renderFullscreenUI = () => {
    if (!isFullscreen) return null;
    return (
      <div className="fixed inset-0 z-[1000] pointer-events-none animate-fade-in">
        <div className="absolute top-4 left-4 text-white text-lg font-bold drop-shadow-lg">
          Loggerhead turtle
        </div>
        <PreviewInteractionHint />
      </div>
    );
  };

  const renderSectionBody = (section: {
    title: string;
    image: string;
    imageAlt: string;
    imageCaption?: React.ReactNode;
    useImageCaptionSpacer?: boolean;
    paragraphs: Array<string | { text: string; italic?: boolean }>;
  }) => (
    <div className="flex flex-col gap-6 md:gap-[32px] 2xl:gap-[40px]">
      <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
        <div className="w-full h-[200px] sm:h-[240px] md:h-[265px] overflow-hidden bg-gray-300">
          <img
            src={section.image}
            alt={section.imageAlt}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        {section.imageCaption ? (
          <p
            aria-hidden={section.useImageCaptionSpacer || undefined}
            className={`${section.useImageCaptionSpacer ? "hidden md:block md:invisible " : ""}text-black text-[12px] font-light leading-[1.2em] italic`}
          >
            {section.imageCaption}
          </p>
        ) : null}
      </div>
      <h3
        className="text-black text-[20px] sm:text-[24px] 2xl:text-[28px]"
        style={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500,
          lineHeight: "1.2em",
        }}
      >
        {section.title}
      </h3>
      <div className="flex flex-col gap-2 sm:gap-3">
        {section.paragraphs.map((paragraph, i) => {
          const html = typeof paragraph === "string" ? paragraph : paragraph.text;
          const fontStyle =
            typeof paragraph === "string" ? "normal" : paragraph.italic ? "italic" : "normal";

          return (
            <p
              key={i}
              className="text-black text-[14px] sm:text-[16px] 2xl:text-[18px]"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 300,
                lineHeight: "1.2em",
                fontStyle,
              }}
              dangerouslySetInnerHTML={{ __html: html }}
            />
          );
        })}
      </div>
    </div>
  );

  return (
    <section
      id={getModelHash(model)}
      className="section-lazy relative overflow-hidden bg-white py-12 px-6 sm:py-16 sm:px-10 md:py-20 md:px-20 2xl:py-[120px] 2xl:px-[155px]"
    >
      {renderFullscreenUI()}

      <div className="flex flex-col w-full max-w-[1130px] mx-auto gap-8 sm:gap-10 md:gap-[60px]">
        <div className="flex flex-col gap-1 sm:gap-2 md:gap-2 2xl:gap-3">
          <h2
            className="text-black text-left font-[600] text-[24px] sm:text-[28px] md:text-[32px] 2xl:text-[40px]"
            style={{
              lineHeight: "1em",
              letterSpacing: "-0.72px",
            }}
          >
          Loggerhead turtle
          </h2>
          <h3 className="leading-[1rem] font-light text-[18px] sm:text-[20px] md:text-[24px] 2xl:text-[30px] italic">
            {model.scientificName}
          </h3>
        </div>

        <div className="flex flex-col gap-6 sm:gap-[30px]">
          <div className="flex flex-col gap-[10px] md:gap-[12px] 2xl:gap-[14px]">
            {renderModelViewer()}
            <a
              href="https://sketchfab.com/3d-models/model-47a-loggerhead-sea-turtle-c438e81e796d41d9a6ae4cc147ef8d4f"
              target="_blank"
              rel="noopener noreferrer"
              className="text-black text-[12px] font-light leading-[1.2em] italic underline hover:opacity-70"
            >
              sketchfab
            </a>
          </div>
          <div className="w-full flex justify-center">
            <ARButton
              model={model}
              onLaunchAR={handleLaunchAR}
              onTrackLaunchClick={onTrackLaunchClick}
              isChecking={isChecking}
              isCompatible={isCompatible}
            />
          </div>
        </div>

        <div className="flex flex-col gap-10 sm:gap-[50px]">
          <div className="flex flex-col md:flex-row gap-8 md:gap-[32px] 2xl:gap-[40px]">
            <div className="flex-1">
              {renderSectionBody(loggerheadCopy.row1.left)}
            </div>
            <div className="flex-1">{renderSectionBody(loggerheadCopy.row1.right)}</div>
          </div>
          <div className="flex flex-col md:flex-row gap-8 md:gap-[32px] 2xl:gap-[40px]">
            <div className="flex-1">{renderSectionBody(loggerheadCopy.row2.left)}</div>
            <div className="flex-1">{renderSectionBody(loggerheadCopy.row2.right)}</div>
          </div>
        </div>
      </div>
    </section>
  );
}
